import { Chess } from "@vendor/chess.js";
import type { ChessConstructor } from "../chess/chessTypes.ts";
import { positionKey } from "../chess/positionKey.ts";
import type { OpeningId } from "../lessons/ids.ts";
import { buildCourse, assertValidCourse, type AuthoredLine } from "../lessons/repository.ts";
import type { LessonCourse } from "../lessons/schema.ts";
import type { BookCoachingNote } from "../trainer/bookCoaching.ts";
import { BLACK_OPENING_IDS, type BlackOpeningId } from "./black/catalog.ts";
import { loadCourseCopy } from "./catalogCopy.ts";
import type { BlackCourse, BlackCourseBody, BlackLesson } from "./black/types.ts";
import { manifestByOpeningId } from "./manifest.ts";

export { BLACK_OPENING_IDS, type BlackOpeningId };
export type { BlackCourse } from "./black/types.ts";

// Representative teaching lines, not a promise that either side must follow
// one sequence. Every branch is matched by position, never by move number.
// Each course body is its own chunk, downloaded the first time the course opens,
// so pages that only list courses never fetch Black course prose.
const LOADERS: Record<BlackOpeningId, () => Promise<BlackCourseBody>> = {
  caroKann: () => import("./black/caroKann.ts").then((m) => m.CARO_KANN),
  qgdBlack: () => import("./black/qgdBlack.ts").then((m) => m.QGD_BLACK),
  symmetricalEnglish: () =>
    import("./black/symmetricalEnglish.ts").then((m) => m.SYMMETRICAL_ENGLISH),
  openGame: () => import("./black/openGame.ts").then((m) => m.OPEN_GAME),
  sicilianNajdorf: () => import("./black/sicilianNajdorf.ts").then((m) => m.SICILIAN_NAJDORF),
  kingsIndian: () => import("./black/kingsIndian.ts").then((m) => m.KINGS_INDIAN),
  frenchDefense: () => import("./black/frenchDefense.ts").then((m) => m.FRENCH_DEFENSE),
  kingsEnglish: () => import("./black/kingsEnglish.ts").then((m) => m.KINGS_ENGLISH),
  petroffDefense: () => import("./black/petroffDefense.ts").then((m) => m.PETROFF_DEFENSE),
  philidorDefense: () => import("./black/philidorDefense.ts").then((m) => m.PHILIDOR_DEFENSE),
  slavDefense: () => import("./black/slavDefense.ts").then((m) => m.SLAV_DEFENSE),
  nimzoIndian: () => import("./black/nimzoIndian.ts").then((m) => m.NIMZO_INDIAN),
  scandinavianDefense: () =>
    import("./black/scandinavianDefense.ts").then((m) => m.SCANDINAVIAN_DEFENSE),
  pircDefense: () => import("./black/pircDefense.ts").then((m) => m.PIRC_DEFENSE),
  qgaBlack: () => import("./black/qgaBlack.ts").then((m) => m.QGA_BLACK),
  twoKnights: () => import("./black/twoKnights.ts").then((m) => m.TWO_KNIGHTS),
  englishNf6: () => import("./black/englishNf6.ts").then((m) => m.ENGLISH_NF6),
  retiBlack: () => import("./black/retiBlack.ts").then((m) => m.RETI_BLACK),
  classicalSicilian: () => import("./black/classicalSicilian.ts").then((m) => m.CLASSICAL_SICILIAN),
  kanSicilian: () => import("./black/kanSicilian.ts").then((m) => m.KAN_SICILIAN),
  berlinDefense: () => import("./black/berlinDefense.ts").then((m) => m.BERLIN_DEFENSE),
  steinitzDefense: () => import("./black/steinitzDefense.ts").then((m) => m.STEINITZ_DEFENSE),
  classicalRuy: () => import("./black/classicalRuy.ts").then((m) => m.CLASSICAL_RUY),
};

const loaded = new Map<BlackOpeningId, BlackCourse>();
const pending = new Map<BlackOpeningId, Promise<BlackCourse>>();

export function isBlackOpening(id: string | null | undefined): id is BlackOpeningId {
  return Boolean(id && (BLACK_OPENING_IDS as readonly string[]).includes(id));
}

/** Download a Black course's body and copy (once) and combine them with its manifest facts. */
export function loadBlackCourse(id: BlackOpeningId): Promise<BlackCourse> {
  const done = loaded.get(id);
  if (done) return Promise.resolve(done);
  let request = pending.get(id);
  if (!request) {
    request = Promise.all([LOADERS[id](), loadCourseCopy()]).then(([body, copy]) => {
      const manifest = manifestByOpeningId(id);
      // Project only teaching copy: a guide's `branches` is prose, whereas a
      // playable course's `branches` is move data. Never spread the whole guide.
      const { intro, principles } = copy.guides[id];
      const course: BlackCourse = {
        ...body,
        tagline: manifest?.tagline || "",
        intro,
        principles,
        slug: manifest?.slug || id,
        title: manifest?.title || id,
        eco: manifest?.catalogEco || "",
      };
      loaded.set(id, course);
      return course;
    });
    // A failed download (say, offline) may be retried later.
    request.catch(() => pending.delete(id));
    pending.set(id, request);
  }
  return request;
}

export function loadAllBlackCourses(): Promise<BlackCourse[]> {
  return Promise.all(BLACK_OPENING_IDS.map(loadBlackCourse));
}

/** The course if it has been downloaded; coaching lookups return nothing until then. */
export function loadedBlackCourse(id: string | null | undefined): BlackCourse | null {
  return isBlackOpening(id) ? loaded.get(id) || null : null;
}

/** The course, which must already be downloaded with loadBlackCourse. */
export function getBlackCourse(id: BlackOpeningId): BlackCourse {
  const course = loaded.get(id);
  if (!course) throw new Error(`Black course ${id} is not loaded; await loadBlackCourse first`);
  return course;
}

type Concept = NonNullable<AuthoredLine["move"]["concepts"]>[number];

function conceptsFor(chess: Chess, san: string): Concept[] {
  if (/^O-O/.test(san)) return ["castle"];
  const move = new Chess(chess.fen()).move(san);
  if (!move) return ["flexible"];
  if (move.piece === "p") return "cdef".includes(move.to[0]) ? ["center"] : ["pawn-structure"];
  if ((move.piece === "n" || move.piece === "b") && /[18]$/.test(move.from)) return ["development"];
  return ["flexible"];
}

function mainNote(moveNumber: number, san: string, lesson: BlackLesson): BookCoachingNote {
  return {
    moveNumber,
    san,
    goal: lesson.goal,
    piece: lesson.piece,
    candidates: lesson.candidates,
    why: lesson.why,
    next: lesson.next,
    watch: lesson.watch,
    ...(lesson.branches ? { branches: lesson.branches } : {}),
    ...(lesson.options?.length
      ? { options: lesson.options.map((option) => ({ ...option, moveNumber })) }
      : {}),
    depth: {
      strategy: lesson.strategy,
      tradeoff: lesson.tradeoff,
      blackPlan: lesson.whitePlan,
      takeaway: lesson.takeaway,
    },
  };
}

type AuthoredCourse = { lines: AuthoredLine[]; notes: Map<string, BookCoachingNote> };

/** Walk the authored lines once, producing both the graph edges and the coaching notes. */
function authorCourse(ChessCtor: ChessConstructor, id: BlackOpeningId): AuthoredCourse {
  const data = getBlackCourse(id);
  const lines: AuthoredLine[] = [];
  const notes = new Map<string, BookCoachingNote>();
  const reached = new Set<string>();
  const setNote = (fen: string, note: BookCoachingNote) => {
    const key = positionKey(fen);
    if (!notes.has(key)) notes.set(key, note);
  };
  const replay = (sans: string[], where: string): Chess => {
    const chess = new ChessCtor() as unknown as Chess;
    for (const san of sans) {
      if (!chess.move(san)) throw new Error(`${id}: illegal ${san} in ${where}`);
    }
    return chess;
  };

  const sans = data.mainline.split(" ");
  if (sans.length !== 30 || data.lessons.length !== 15) {
    throw new Error(`${id}: the main line needs 30 plies and 15 lessons`);
  }
  const main = replay([], "main line");
  reached.add(positionKey(main.fen()));
  // Positions where White already has a course reply. A branch's first White
  // move there is the deviation (a side line); its later White moves are the
  // branch's own line, so the graph keeps following them in every mode.
  const whiteReplies = new Set<string>();
  sans.forEach((san, index) => {
    const lesson = data.lessons[Math.floor(index / 2)];
    const after = sans.slice(0, index);
    const concepts = conceptsFor(main, san);
    if (index % 2 === 0) {
      whiteReplies.add(positionKey(main.fen()));
      lines.push({
        after,
        move: {
          san,
          role: "mainline",
          explanation: lesson.white,
          concepts,
          variationName: data.title,
          opponentIntent: lesson.white,
        },
      });
    } else {
      lines.push({
        after,
        move: {
          san,
          role: "repertoire",
          explanation: lesson.why,
          concepts,
          variationName: data.title,
          objective: `Play …${san}. ${lesson.goal}`,
        },
      });
      setNote(main.fen(), mainNote((index + 1) / 2, san, lesson));
      for (const option of lesson.options || []) {
        lines.push({
          after,
          move: {
            san: option.san,
            role: "sideline",
            explanation: option.why,
            concepts: conceptsFor(main, option.san),
            variationName: option.name,
          },
        });
      }
    }
    if (!main.move(san)) throw new Error(`${id}: illegal ${san} in the main line`);
    reached.add(positionKey(main.fen()));
  });

  for (const branch of [...data.branches, ...(data.entries ?? [])]) {
    const prefix = branch.after ? branch.after.split(" ") : [];
    const moves = branch.moves.split(" ");
    const chess = replay(prefix, branch.name);
    if (!reached.has(positionKey(chess.fen()))) {
      throw new Error(
        `${id}: branch ${branch.name} starts from a position no earlier line reaches`
      );
    }
    let whiteIndex = 0;
    let blackIndex = 0;
    moves.forEach((san, j) => {
      const after = [...prefix, ...moves.slice(0, j)];
      const concepts = conceptsFor(chess, san);
      if (chess.turn() === "w") {
        const text = branch.white[whiteIndex++];
        if (!text) throw new Error(`${id}: ${branch.name} needs a note for White's ${san}`);
        const key = positionKey(chess.fen());
        const deviation = whiteReplies.has(key);
        whiteReplies.add(key);
        lines.push({
          after,
          move: {
            san,
            role: deviation ? "sideline" : "mainline",
            explanation: text,
            concepts,
            variationName: branch.name,
            opponentIntent: text,
            ...(branch.eco ? { eco: branch.eco } : {}),
          },
        });
      } else {
        const lesson = branch.lessons[blackIndex++];
        if (!lesson) throw new Error(`${id}: ${branch.name} needs a lesson for …${san}`);
        lines.push({
          after,
          move: {
            san,
            role: "repertoire",
            explanation: lesson.why,
            concepts,
            variationName: branch.name,
            objective: `Play …${san}. ${lesson.goal}`,
          },
        });
        setNote(chess.fen(), {
          moveNumber: Math.floor(after.length / 2) + 1,
          san,
          goal: lesson.goal,
          piece: lesson.piece,
          candidates: lesson.candidates,
          why: lesson.why,
          next: lesson.next,
          watch: lesson.watch,
        });
      }
      if (!chess.move(san)) throw new Error(`${id}: illegal ${san} in ${branch.name}`);
      reached.add(positionKey(chess.fen()));
    });
    if (whiteIndex !== branch.white.length || blackIndex !== branch.lessons.length) {
      throw new Error(`${id}: ${branch.name} has notes that match no move`);
    }
  }

  for (const trap of data.traps) {
    const prefix = trap.after ? trap.after.split(" ") : [];
    const chess = replay(prefix, trap.name);
    if (!reached.has(positionKey(chess.fen())) || chess.turn() !== "b") {
      throw new Error(`${id}: trap ${trap.name} must start at a Black decision in the course`);
    }
    if (!new Chess(chess.fen()).move(trap.san)) {
      throw new Error(`${id}: illegal trap move ${trap.san} in ${trap.name}`);
    }
    lines.push({
      after: prefix,
      move: {
        san: trap.san,
        role: "mistake",
        explanation: trap.why,
        concepts: ["tempo"],
        tacticalWarning: true,
        variationName: trap.name,
      },
    });
  }
  return { lines, notes };
}

/** The course objectives, which the catalog also shows as the course's principles. */
export function blackObjectives(id: BlackOpeningId): string[] {
  return getBlackCourse(id).principles;
}

export function buildBlackCourse(ChessCtor: ChessConstructor, id: BlackOpeningId): LessonCourse {
  const data = getBlackCourse(id);
  const manifest = manifestByOpeningId(id);
  const { lines } = authorCourse(ChessCtor, id);
  const course = buildCourse(ChessCtor, {
    id: `${data.slug}-15`,
    openingId: id,
    slug: data.slug,
    title: data.title,
    side: "black",
    targetFullMoves: 15,
    level: manifest?.level || "beginner",
    objectives: blackObjectives(id),
    contentVersion: 2,
    rootObjective: "White moves first; then you choose Black’s response.",
    rootExplanation: data.intro,
    lines,
    reviewStatus: manifest?.reviewStatus || "draft",
    limitedCoverage: false,
    branchPolicy: "common",
  });
  assertValidCourse(course, ChessCtor);
  return course;
}

// Notes are derived from the authored lines on first use, like the White books,
// so a lookup never depends on whether the course catalog was built first.
const notesByCourse = new Map<BlackOpeningId, Map<string, BookCoachingNote>>();
function notesFor(id: BlackOpeningId): Map<string, BookCoachingNote> {
  let notes = notesByCourse.get(id);
  if (!notes) {
    if (!loaded.has(id)) return new Map();
    notes = authorCourse(Chess as unknown as ChessConstructor, id).notes;
    notesByCourse.set(id, notes);
  }
  return notes;
}

export function blackBookAtPosition(
  id: OpeningId | string | null | undefined,
  fen: string,
  san?: string | null
): BookCoachingNote | null {
  if (!isBlackOpening(id)) return null;
  const note = notesFor(id).get(positionKey(fen)) || null;
  return note && (!san || note.san.replace(/[+#]/g, "") === san.replace(/[+#]/g, "")) ? note : null;
}
export function blackBookFor(id: BlackOpeningId, moveNumber: number): BookCoachingNote | null {
  return [...notesFor(id).values()].find((note) => note.moveNumber === moveNumber) || null;
}
